/**
 * Drivers Tab Component
 * Main driver management view with filters and summary stats
 */

import { useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { useDrivers } from '../../../hooks/useDrivers';
import { DriverTable } from './DriverTable';
import { CreateDriverModal } from './CreateDriverModal';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import type { DriverStatus } from '../../../services/driver.service';

export function DriversTab() {
  const [statusFilter, setStatusFilter] = useState<DriverStatus | undefined>(undefined);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data, isLoading, error } = useDrivers({ status: statusFilter });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        Error loading drivers: {error.message}
      </div>
    );
  }

  const drivers = data?.drivers || [];
  const stats = data?.byStatus || { active: 0, inactive: 0, on_leave: 0 };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card className="p-4">
          <div className="text-sm font-medium text-gray-500">Active</div>
          <div className="mt-2 text-2xl font-bold">{stats.active}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm font-medium text-gray-500">On Leave</div>
          <div className="mt-2 text-2xl font-bold">{stats.on_leave}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm font-medium text-gray-500">Inactive</div>
          <div className="mt-2 text-2xl font-bold">{stats.inactive}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm font-medium text-gray-500">Total</div>
          <div className="mt-2 text-2xl font-bold">{data?.total || 0}</div>
        </Card>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <select
            value={statusFilter || ''}
            onChange={(e) => setStatusFilter(e.target.value as DriverStatus || undefined)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="on_leave">On Leave</option>
            <option value="inactive">Inactive</option>
          </select>

          {statusFilter && (
            <button
              onClick={() => setStatusFilter(undefined)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Clear Filter
            </button>
          )}
        </div>

        <Button onClick={() => setIsCreateModalOpen(true)} className="whitespace-nowrap">
          <Plus className="mr-2 h-4 w-4" />
          Add Driver
        </Button>
      </div>

      {/* Driver Table */}
      {drivers.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="text-gray-500">
            {statusFilter
              ? 'No drivers match the selected filter'
              : 'No drivers yet. Add your first driver to get started.'}
          </div>
          {!statusFilter && (
            <Button onClick={() => setIsCreateModalOpen(true)} className="mt-4">
              <Plus className="mr-2 h-4 w-4" />
              Add Driver
            </Button>
          )}
        </Card>
      ) : (
        <DriverTable drivers={drivers} />
      )}

      {/* Create Modal */}
      <CreateDriverModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
}
